import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../services/theme.service';
import { Observable, of, switchMap, tap } from 'rxjs';
import { FilterOrSortDropdownType, FilterSelectedEventType } from '@shared/models/types';
import { BannerFilter } from '@shared/entities/bannerFilter';
import { BannerFilterURL, BannerService } from '@shared/services/api/banner.service';
import { BannerFiltersService } from '@shared/services/api/banner-filter.service';
import { Banner } from '@shared/entities/banner';

@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss'
})
export class BannerComponent implements OnInit {
  banners$: Observable<Banner[]>;
  prevPage?: number = undefined;
  currentPage? = 1;
  nextPage?: number = undefined;
  totalPages?: number = undefined;

  bannerFilter$: Observable<BannerFilter>;
  isLight = true;

  filters: any = {};

  constructor(
    public translateService: TranslateService,
    private _themeService: ThemeService,
    private _bannerService: BannerService,
    private _bannerFilterService: BannerFiltersService
  ) {
    this.banners$ = this._bannerService.allBanners$.pipe(
      switchMap((pagination) => {
        this.prevPage = pagination.prevPage;
        this.currentPage = pagination.currentPage;
        this.nextPage = pagination.nextPage;
        this.totalPages = pagination.totalPage;
        return of(pagination.data);
      })
    );

    this.bannerFilter$ = this._bannerFilterService.all$;
  }

  ngOnInit() {
    this._themeService.refresh$
      .pipe(switchMap(() => this._themeService.isDarkTheme$.pipe(tap((isDarkTheme) => (this.isLight = !isDarkTheme)))))
      .subscribe();
  }

  onSelectedFilter(filters: FilterSelectedEventType[]) {
    const filtersName = filters.reduce((acc: any, filter: FilterSelectedEventType) => {
      if (filter.name === 'date' && filter.selectedValue) {
        acc[filter.name] = filter.selectedValue;
      } else {
        acc[filter.name] = filter.selectedValue;
      }
      return acc;
    }, {});

    this.filters = { ...filtersName };

    const bannerFilters: BannerFilterURL = {};

    if (filtersName.keyword) bannerFilters.keyword = filtersName.keyword;
    if (filtersName.geoLocation) bannerFilters.geoLocation = filtersName.geoLocation;
    if (filtersName.operatingSector) bannerFilters.operatingSector = filtersName.operatingSector;
    if (filtersName.date) bannerFilters.date = filtersName.date;
    if (filtersName.orderBy) bannerFilters.orderBy = filtersName.orderBy;
    if (filtersName.id) bannerFilters.id = filtersName.id;

    this._bannerService.filters = bannerFilters;
  }

  onBannerPage(page: number) {
    this._bannerService
      .refresh(page)
      .pipe(
        tap((pagination) => {
          this.prevPage = pagination.prevPage;
          this.currentPage = pagination.currentPage;
          this.nextPage = pagination.nextPage;
        })
      )
      .subscribe();
  }

  getFilterFields(filterData: any): FilterOrSortDropdownType[] {
    const fields: FilterOrSortDropdownType[] = [];

    if (!filterData) {
      return fields;
    }

    for (const key in filterData) {
      if (Object.prototype.hasOwnProperty.call(filterData, key) && key !== 'orderBy') {
        const values = filterData[key];
        if (values && Array.isArray(values)) {
          fields.push({
            key: key,
            values: values.map((value: string) => ({
              id: value,
              value: value
            }))
          });
        }
      }
    }

    return fields;
  }

  getSortingOptions(filterData: any): FilterOrSortDropdownType[] {
    const options: FilterOrSortDropdownType[] = [];

    if (!filterData || !filterData.orderBy) {
      return options;
    }

    options.push({
      key: 'orderBy',
      values: filterData.orderBy.map((value: string) => ({
        id: value,
        value: value
      }))
    });

    return options;
  }

  resetFilters() {
    this._bannerFilterService.filters = {} as BannerFilterURL;
  }
}
